import type { ReactNode } from 'react';
import { Icon } from './Icon';

interface EntryLayoutProps {
  /** Branded left-panel headline. */
  tagline: string;
  /** Branded left-panel sub-copy. */
  description: string;
  /** Optional numbered steps shown under the description. */
  steps?: string[];
  /** Right-panel content (form, confirmation, error). */
  children: ReactNode;
}

/**
 * Split-screen branded layout shared by the request, link-sent, verify, and
 * session-expired screens (AC3.2). Branded blue gradient on the left, white
 * content panel on the right.
 */
export function EntryLayout({ tagline, description, steps, children }: EntryLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <div className="flex w-full flex-col justify-center bg-entry-gradient p-12 text-white md:w-[48%] md:p-16">
        <div className="mb-12 flex items-center gap-2 text-lg font-extrabold">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20">
            <Icon name="logoWhite" size={14} />
          </span>
          Vane Loop
        </div>
        <h1 className="mb-3 text-4xl font-extrabold leading-tight tracking-tight">
          {tagline}
        </h1>
        <p className="text-sm leading-relaxed opacity-80">{description}</p>
        {steps && steps.length > 0 && (
          <ol className="mt-11 flex flex-col gap-3">
            {steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm leading-snug opacity-90">
                <span className="mt-0.5 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-bold">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        )}
      </div>
      <div className="flex w-full flex-col justify-center bg-white p-12 md:w-[52%] md:px-16">
        {children}
      </div>
    </div>
  );
}
